import { Injectable } from "@angular/core";
import { ApplicantInfo, IApplicantInfo, AppLoadError } from "../shared/models";
import {
  BehaviorSubject,
  catchError,
  finalize,
  map,
  Observable,
  throwError,
} from "rxjs";
import { ApplicantInfoService } from "../shared/services/api/applicant-info.service";
import { AppStateService } from "../shared/services/app-state.service";

@Injectable({
  providedIn: "root",
})
export class ApplicantInfoStoreService {
  // State management BehaviorSubjects
  private readonly _applicant = new BehaviorSubject<IApplicantInfo>(null);
  private readonly _loading = new BehaviorSubject<boolean>(false);
  private readonly _error = new BehaviorSubject<AppLoadError>(null);

  // Expose observables for external subscribers
  readonly applicant$ = this._applicant.asObservable();
  readonly loading$ = this._loading.asObservable();
  readonly error$ = this._error.asObservable();

  // Getter for current applicant state
  get applicantInfo(): IApplicantInfo {
    return this._applicant.getValue();
  }

  // Setter for applicant state
  private set applicantInfo(val: IApplicantInfo) {
    this._applicant.next(val);
  }

  // Constructor
  constructor(
    private applicantInfoService: ApplicantInfoService,
    private appState: AppStateService
  ) {}

  /**
   * Selector for current application ID
   */
  selectApplicationID(): Observable<string | null> {
    return this.applicant$.pipe(
      map((applicant) => (applicant ? applicant.appId : null))
    );
  }

  /**
   * Selector for the last step completed by the user
   */
  selectLastStep(): Observable<number | null> {
    return this.applicant$.pipe(
      map((applicant) => (applicant ? applicant.lastStep : null))
    );
  }

  /**
   * Selector for the current applicant info
   */
  selectApplicantInfo(): Observable<IApplicantInfo | null> {
    return this.applicant$.pipe(map((applicantInfo) => applicantInfo));
  }

  // Select the loading state
  selectLoading(): Observable<boolean> {
    return this.loading$.pipe(map((loading) => loading));
  }

  // Select the error state
  selectError(): Observable<AppLoadError | null> {
    return this.error$.pipe(map((error) => error));
  }

  /**
   * Loads applicant info from the API and updates the store
   * @GET
   * @param payload The payload to fetch applicant info
   */
  loadApplicantInfo(payload: string = null): Observable<IApplicantInfo> {
    this._loading.next(true); // Start loading

    return this.applicantInfoService.getApplicantInfo(payload).pipe(
      map((applicantInfo) => {
        if (!applicantInfo || Object.entries(applicantInfo).length === 0) {
          // Create an empty applicant info object for new applicants
          applicantInfo = new ApplicantInfo();
        }
        this._error.next(null); // Clear previous errors

        this._applicant.next(applicantInfo); // Update the store
        this.appState.applicantInfo = applicantInfo;
        return applicantInfo;
      }),
      catchError((err) => {
        const error = err instanceof ErrorEvent ? err.error : err;
        console.error("Error loading applicant info:", error);

        const displayError = error
          ? { id: error.id, message: error.message }
          : { id: "unknown", message: "An unknown error occurred." };

        this._error.next(displayError as AppLoadError); // Store error
        this.applicantInfo = null; // Clear applicant info on error
        return throwError(
          () => new AppLoadError(displayError.id, displayError.message)
        );
      }),
      finalize(() => this._loading.next(false)) // Stop loading
    );
  }

  /**
   * Clears the applicant info store
   */
  clearApplicantInfo() {
    this._applicant.next(null); // Reset applicant info
    this._loading.next(false); // Reset loading state
    this._error.next(null); // Reset error state
  }
}
